// src/screens/ClientLoader.tsx
// Brief 28 — Client Loader & Session Start Flow
// Adviser-only opening screen. Three paths: intake fetch, manual entry, demo.
// After client is loaded → Session Selector → ClientWelcome.

import React, { useState, useCallback } from 'react';
import { ClientData, BLANK_CLIENT } from '../types/ClientData';
import { SARAH_MITCHELL, CLAIRE_HENDERSON } from '../data/demoClients';

// ── Props ────────────────────────────────────────────────────────────────────
interface ClientLoaderProps {
  onLoad: (data: ClientData) => void;
  onSessionSelect: (session: 1 | 2 | 3) => void;
}

// ── Form data (6-step manual entry) ─────────────────────────────────────────
interface FormData {
  // Step 1 — Client
  firstName: string;
  lastName: string;
  clientAge: string;
  targetFreedomAge: string;
  isCouple: boolean;
  partnerFirstName: string;
  partnerLastName: string;
  partnerAge: string;
  partnerTargetFreedomAge: string;
  // Step 2 — Business
  businessName: string;
  adviserName: string;
  // Step 3 — Income
  salary: string;
  dividends: string;
  otherIncome: string;
  expensesReimbursement: string;
  taxPaymentMethod: 'monthly' | 'selfAssessment' | 'accountant';
  // Step 4 — Pension & ISA
  pensionBalance: string;
  pensionMonthly: string;
  pensionCompanyFunded: string;
  isaBalance: string;
  isaContribution: string;
  // Step 5 — Emergency Fund
  efAccount1Name: string;
  efAccount1Balance: string;
  efAccount1Rate: string;
  efAccount1Monthly: string;
  showSecondAccount: boolean;
  efAccount2Name: string;
  efAccount2Balance: string;
  efAccount2Rate: string;
  efAccount2Monthly: string;
  // Step 6 — Property & Living
  propertyValue: string;
  mortgageBalance: string;
  mortgagePayment: string;
  mortgageOP: string;
  livingCosts: string;
  sinkingFundsTotal: string;
}

const INITIAL_FORM: FormData = {
  firstName: '', lastName: '', clientAge: '', targetFreedomAge: '60',
  isCouple: false, partnerFirstName: '', partnerLastName: '', partnerAge: '', partnerTargetFreedomAge: '',
  businessName: '', adviserName: 'Peter Fuller',
  salary: '0', dividends: '0', otherIncome: '0', expensesReimbursement: '0',
  taxPaymentMethod: 'selfAssessment',
  pensionBalance: '0', pensionMonthly: '0', pensionCompanyFunded: '0',
  isaBalance: '0', isaContribution: '0',
  efAccount1Name: 'Savings account', efAccount1Balance: '0', efAccount1Rate: '0', efAccount1Monthly: '0',
  showSecondAccount: false,
  efAccount2Name: 'Savings account 2', efAccount2Balance: '0', efAccount2Rate: '0', efAccount2Monthly: '0',
  propertyValue: '0', mortgageBalance: '0', mortgagePayment: '0', mortgageOP: '0',
  livingCosts: '0', sinkingFundsTotal: '0',
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const num = (v: string): number => {
  const n = parseFloat(v.replace(/[^0-9.\-]/g, ''));
  return isNaN(n) ? 0 : n;
};

const fmt = (v: number): string =>
  v === 0 ? '£0' : '£' + v.toLocaleString('en-GB', { maximumFractionDigits: 0 });

// ── Shared styles (module-level so references are stable) ────────────────────
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 14px', background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', color: 'white',
  fontSize: '15px', fontFamily: 'Montserrat, sans-serif', outline: 'none',
  transition: 'border-color 0.2s',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'rgba(255,255,255,0.5)', marginBottom: '6px', fontWeight: 500,
};
const noteStyle: React.CSSProperties = {
  fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '6px',
};

// ── Stable sub-components (defined outside ClientLoader to avoid remount) ────
function Field({ label, value, field, type = 'text', prefix, suffix, note, onChange }: {
  label: string; value: string; field: string; type?: string;
  prefix?: string; suffix?: string; note?: string;
  onChange: (field: string, value: string) => void;
}) {
  return (
    <div style={{ marginBottom: '18px' }}>
      <label style={labelStyle}>{label}</label>
      <div style={{ position: 'relative' }}>
        {prefix && <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)', fontSize: '15px', pointerEvents: 'none' }}>{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={e => onChange(field, e.target.value)}
          style={{ ...inputStyle, paddingLeft: prefix ? '30px' : '14px', paddingRight: suffix ? '50px' : '14px' }}
          onFocus={e => { e.currentTarget.style.borderColor = '#64DAD2'; }}
          onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
        />
        {suffix && <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)', fontSize: '13px', pointerEvents: 'none' }}>{suffix}</span>}
      </div>
      {note && <p style={noteStyle}>{note}</p>}
    </div>
  );
}

function Toggle({ label, value, field, onChange }: {
  label: string; value: boolean; field: string;
  onChange: (field: string, value: boolean) => void;
}) {
  return (
    <div style={{ marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <button
        onClick={() => onChange(field, !value)}
        style={{
          width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
          background: value ? '#64DAD2' : 'rgba(255,255,255,0.15)', position: 'relative',
          transition: 'background 0.2s',
        }}
      >
        <span style={{
          position: 'absolute', top: '3px', left: value ? '25px' : '3px', width: '20px', height: '20px',
          borderRadius: '50%', background: 'white', transition: 'left 0.2s',
        }} />
      </button>
      <span style={{ color: 'white', fontSize: '14px' }}>{label}</span>
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────
type Screen = 'menu' | 'manual' | 'review' | 'session-select' | 'checklist';

export default function ClientLoader({ onLoad, onSessionSelect }: ClientLoaderProps) {
  const [screen, setScreen] = useState<Screen>('menu');
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>({ ...INITIAL_FORM });
  const [intakeName, setIntakeName] = useState('');
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intakeError, setIntakeError] = useState('');
  const [loadedData, setLoadedData] = useState<ClientData | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [selectedSession, setSelectedSession] = useState<1 | 2 | 3 | null>(null);
  const [checkVoyant, setCheckVoyant] = useState(false);
  const [checkStandingOrders, setCheckStandingOrders] = useState(false);

  const TOTAL_STEPS = 7; // 6 entry + 1 review

  // ── Intake fetch ──────────────────────────────────────────────────────────
  const handleIntakeFetch = async () => {
    if (!intakeName.trim()) return;
    setIntakeLoading(true);
    setIntakeError('');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(
        `https://eo8gjbbxknt28ey.m.pipedream.net?name=${encodeURIComponent(intakeName.trim())}`,
        { signal: controller.signal }
      );
      clearTimeout(timeout);
      if (!res.ok) throw new Error('not found');
      const json = await res.json();
      if (!json || !json.clientName) {
        setIntakeError(`No intake submission found for '${intakeName.trim()}'. Try entering details manually.`);
        return;
      }
      const merged: ClientData = { ...BLANK_CLIENT, ...json, sessionDate: new Date().toLocaleDateString('en-GB') };
      setLoadedData(merged);
      setIsDemo(false);
      setScreen('session-select');
    } catch (err: any) {
      clearTimeout(timeout);
      if (err.name === 'AbortError') {
        setIntakeError('Connection error — check internet and try again, or enter details manually.');
      } else {
        setIntakeError(`No intake submission found for '${intakeName.trim()}'. Try entering details manually.`);
      }
    } finally {
      setIntakeLoading(false);
    }
  };

  // ── Manual form → ClientData ──────────────────────────────────────────────
  const formToClientData = (): ClientData => ({
    ...BLANK_CLIENT,
    clientName: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
    clientAge: num(form.clientAge),
    targetFreedomAge: num(form.targetFreedomAge),
    isCouple: form.isCouple,
    partnerName: form.isCouple ? `${form.partnerFirstName.trim()} ${form.partnerLastName.trim()}`.trim() : '',
    partnerAge: form.isCouple ? num(form.partnerAge) : 0,
    sessionDate: new Date().toLocaleDateString('en-GB'),
    sessionType: 'new',
    businessName: form.businessName.trim(),
    adviserName: form.adviserName.trim(),
    salary: num(form.salary),
    dividends: num(form.dividends),
    otherIncome: num(form.otherIncome),
    expensesReimbursement: num(form.expensesReimbursement),
    taxPaymentMethod: form.taxPaymentMethod === 'accountant' ? 'selfAssessment' : form.taxPaymentMethod,
    pensionBalance: num(form.pensionBalance),
    pensionBalanceDC: num(form.pensionBalance),
    pensionMonthly: num(form.pensionMonthly),
    pensionCompanyFunded: num(form.pensionCompanyFunded),
    isaBalance: num(form.isaBalance),
    investmentsBalance: num(form.isaBalance),  // default: assume full ISA is investments
    isaContribution: num(form.isaContribution),
    efAccount1Name: form.efAccount1Name.trim(),
    efAccount1Balance: num(form.efAccount1Balance),
    efAccount1Rate: num(form.efAccount1Rate),
    efAccount1Monthly: num(form.efAccount1Monthly),
    efAccount2Name: form.showSecondAccount ? form.efAccount2Name.trim() : '',
    efAccount2Balance: form.showSecondAccount ? num(form.efAccount2Balance) : 0,
    efAccount2Rate: form.showSecondAccount ? num(form.efAccount2Rate) : 0,
    efAccount2Monthly: form.showSecondAccount ? num(form.efAccount2Monthly) : 0,
    efTargetMonths: 6,
    propertyValue: num(form.propertyValue),
    mortgageBalance: num(form.mortgageBalance),
    mortgagePayment: num(form.mortgagePayment),
    mortgageOP: num(form.mortgageOP),
    livingCosts: num(form.livingCosts),
    sinkingFundsTotal: num(form.sinkingFundsTotal),
  });

  const handleManualConfirm = () => {
    const data = formToClientData();
    setLoadedData(data);
    setIsDemo(false);
    setScreen('session-select');
  };

  // ── Demo load ─────────────────────────────────────────────────────────────
  const handleDemo = (demo: ClientData) => {
    const data = { ...demo, sessionDate: new Date().toLocaleDateString('en-GB') };
    setLoadedData(data);
    setIsDemo(true);
    setScreen('session-select');
  };

  // ── Session select → fire ─────────────────────────────────────────────────
  const handleSessionPick = (session: 1 | 2 | 3) => {
    if (!loadedData) return;
    setSelectedSession(session);
    if (isDemo) {
      // Demo skips checklist — go straight
      onLoad(loadedData);
      onSessionSelect(session);
    } else {
      setScreen('checklist');
    }
  };

  const handleBeginSession = () => {
    if (!loadedData || !selectedSession) return;
    onLoad(loadedData);
    onSessionSelect(selectedSession);
  };

  // ── Update form helper (stable via useCallback) ──────────────────────────
  const up = useCallback((field: string, value: any) => setForm(prev => ({ ...prev, [field]: value })), []);

  // ── Shared styles ─────────────────────────────────────────────────────────
  const cardStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '16px', padding: '28px', flex: '1 1 280px', maxWidth: '320px',
    transition: 'background 0.2s, border-color 0.2s', cursor: 'pointer',
  };
  const tealBtn: React.CSSProperties = {
    width: '100%', padding: '14px', background: '#64DAD2', color: '#0a1628',
    border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: 600,
    cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: MENU (three path cards)
  // ═══════════════════════════════════════════════════════════════════════════
  if (screen === 'menu') {
    return (
      <div style={{
        minHeight: '100vh', background: '#0a1628', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat, sans-serif',
        padding: '40px 24px',
      }}>
        {/* Branding */}
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '8px' }}>
          Motion Accountancy
        </p>
        <h1 style={{ color: 'white', fontSize: '34px', fontWeight: 600, margin: '0 0 48px 0', textAlign: 'center' }}>
          Who are we seeing today?
        </h1>

        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '1020px', width: '100%' }}>

          {/* ── Card A: Load from intake ────────────────────────────────────── */}
          <div style={cardStyle}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ color: '#64DAD2', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Load from intake
            </p>
            <p style={{ color: 'white', fontSize: '18px', fontWeight: 600, marginBottom: '6px' }}>
              Fetch client's intake form submission
            </p>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', marginBottom: '20px' }}>
              Pull data from the client's completed intake form
            </p>
            <input
              type="text"
              placeholder="Client name or reference"
              value={intakeName}
              onChange={e => { setIntakeName(e.target.value); setIntakeError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleIntakeFetch()}
              style={{ ...inputStyle, marginBottom: '12px' }}
              onFocus={e => { e.currentTarget.style.borderColor = '#64DAD2'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
            />
            <button onClick={handleIntakeFetch} disabled={intakeLoading || !intakeName.trim()} style={{
              ...tealBtn, opacity: intakeLoading || !intakeName.trim() ? 0.5 : 1,
            }}>
              {intakeLoading ? 'Fetching…' : 'Fetch intake data →'}
            </button>
            {intakeError && (
              <p style={{ color: '#f87171', fontSize: '13px', marginTop: '10px' }}>{intakeError}</p>
            )}
            <p style={noteStyle}>Fetches from the client's intake form submission</p>
          </div>

          {/* ── Card B: Enter manually ──────────────────────────────────────── */}
          <div style={cardStyle}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
            onClick={() => { setScreen('manual'); setStep(1); }}
          >
            <p style={{ color: '#64DAD2', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Enter details manually
            </p>
            <p style={{ color: 'white', fontSize: '18px', fontWeight: 600, marginBottom: '6px' }}>
              Start fresh — enter key info now
            </p>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', marginBottom: '20px' }}>
              6-step form covering income, pension, property and more
            </p>
            <button style={tealBtn}>Begin →</button>
          </div>

          {/* ── Card C: Demo ────────────────────────────────────────────────── */}
          <div style={{ ...cardStyle, cursor: 'default' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
          >
            <p style={{ color: '#64DAD2', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Run a demo
            </p>
            <p style={{ color: 'white', fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
              Practice with sample data
            </p>
            {[
              { data: CLAIRE_HENDERSON, name: 'Claire Henderson', desc: 'Higher rate · single · full plan' },
              { data: SARAH_MITCHELL, name: 'Sarah Mitchell', desc: 'Basic rate · single · prospect demo' },
            ].map(d => (
              <button
                key={d.name}
                onClick={() => handleDemo(d.data)}
                style={{
                  width: '100%', textAlign: 'left', padding: '12px 14px', marginBottom: '8px',
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '10px', cursor: 'pointer', transition: 'background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              >
                <span style={{ color: 'white', fontSize: '14px', fontWeight: 600, display: 'block' }}>{d.name}</span>
                <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px' }}>{d.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: MANUAL ENTRY FORM (Steps 1–6 + Review)
  // ═══════════════════════════════════════════════════════════════════════════
  if (screen === 'manual' || screen === 'review') {
    const isReview = step === TOTAL_STEPS;

    return (
      <div style={{
        minHeight: '100vh', background: '#0a1628', fontFamily: 'Montserrat, sans-serif',
        display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 24px',
      }}>
        {/* Top bar */}
        <div style={{ width: '100%', maxWidth: '560px', marginBottom: '24px' }}>
          <button
            onClick={() => { setScreen('menu'); setStep(1); }}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', fontSize: '13px', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif', padding: 0 }}
          >
            ← Back to menu
          </button>
        </div>

        {/* Progress */}
        <div style={{ width: '100%', maxWidth: '560px', marginBottom: '8px' }}>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', marginBottom: '8px' }}>
            {isReview ? 'Review & Confirm' : `Step ${step} of 6`}
          </p>
          <div style={{ height: '3px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(step / TOTAL_STEPS) * 100}%`, background: '#64DAD2', transition: 'width 0.3s', borderRadius: '2px' }} />
          </div>
        </div>

        {/* Form content */}
        <div style={{ width: '100%', maxWidth: '560px', marginTop: '24px' }}>

          {/* ── STEP 1: Client ───────────────────────────────────────────── */}
          {step === 1 && (
            <>
              <h2 style={{ color: 'white', fontSize: '22px', fontWeight: 600, marginBottom: '24px' }}>Client</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Field onChange={up} label="First name" value={form.firstName} field="firstName" />
                <Field onChange={up} label="Last name" value={form.lastName} field="lastName" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Field onChange={up} label="Age" value={form.clientAge} field="clientAge" type="number" />
                <Field onChange={up} label="Target freedom age" value={form.targetFreedomAge} field="targetFreedomAge" type="number" />
              </div>
              <Toggle onChange={up} label="Is there a partner?" value={form.isCouple} field="isCouple" />
              {form.isCouple && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <Field onChange={up} label="Partner first name" value={form.partnerFirstName} field="partnerFirstName" />
                    <Field onChange={up} label="Partner last name" value={form.partnerLastName} field="partnerLastName" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <Field onChange={up} label="Partner age" value={form.partnerAge} field="partnerAge" type="number" />
                    <Field onChange={up} label="Partner freedom age" value={form.partnerTargetFreedomAge} field="partnerTargetFreedomAge" type="number" />
                  </div>
                </>
              )}
            </>
          )}

          {/* ── STEP 2: Business ─────────────────────────────────────────── */}
          {step === 2 && (
            <>
              <h2 style={{ color: 'white', fontSize: '22px', fontWeight: 600, marginBottom: '24px' }}>Business</h2>
              <Field onChange={up} label="Business name" value={form.businessName} field="businessName" note="Used on client-facing screens and PDFs" />
              <Field onChange={up} label="Adviser name" value={form.adviserName} field="adviserName" />
            </>
          )}

          {/* ── STEP 3: Income ───────────────────────────────────────────── */}
          {step === 3 && (
            <>
              <h2 style={{ color: 'white', fontSize: '22px', fontWeight: 600, marginBottom: '24px' }}>Income</h2>
              <Field onChange={up} label="Salary (annual)" value={form.salary} field="salary" type="number" prefix="£" suffix="/year" />
              <Field onChange={up} label="Dividends (annual)" value={form.dividends} field="dividends" type="number" prefix="£" suffix="/year" />
              <Field onChange={up} label="Other income (annual)" value={form.otherIncome} field="otherIncome" type="number" prefix="£" suffix="/year" />
              <Field onChange={up} label="Monthly expenses reimbursement" value={form.expensesReimbursement} field="expensesReimbursement" type="number" prefix="£" suffix="/month" />

              <div style={{ marginBottom: '18px' }}>
                <p style={{ ...noteStyle, marginBottom: '10px', marginTop: 0 }}>
                  This determines how the tax reserve appears in the monthly position
                </p>
                <label style={labelStyle}>Tax payment method</label>
                {[
                  { value: 'monthly' as const, label: 'Monthly standing order to HMRC' },
                  { value: 'selfAssessment' as const, label: 'January & July self-assessment' },
                  { value: 'accountant' as const, label: 'My accountant handles it' },
                ].map(opt => (
                  <label key={opt.value} style={{
                    display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
                    marginBottom: '6px', borderRadius: '10px', cursor: 'pointer',
                    background: form.taxPaymentMethod === opt.value ? 'rgba(100,218,210,0.12)' : 'rgba(255,255,255,0.04)',
                    border: form.taxPaymentMethod === opt.value ? '1px solid rgba(100,218,210,0.4)' : '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.15s',
                  }}>
                    <input
                      type="radio"
                      name="taxMethod"
                      checked={form.taxPaymentMethod === opt.value}
                      onChange={() => up('taxPaymentMethod', opt.value)}
                      style={{ accentColor: '#64DAD2' }}
                    />
                    <span style={{ color: 'white', fontSize: '14px' }}>{opt.label}</span>
                  </label>
                ))}
              </div>
            </>
          )}

          {/* ── STEP 4: Pension & ISA ────────────────────────────────────── */}
          {step === 4 && (
            <>
              <h2 style={{ color: 'white', fontSize: '22px', fontWeight: 600, marginBottom: '24px' }}>Pension & ISA</h2>
              <Field onChange={up} label="Pension — current balance" value={form.pensionBalance} field="pensionBalance" type="number" prefix="£" />
              <Field onChange={up} label="Pension — personal monthly contribution" value={form.pensionMonthly} field="pensionMonthly" type="number" prefix="£" suffix="/month" />
              <Field onChange={up} label="Pension — employer (company) monthly" value={form.pensionCompanyFunded} field="pensionCompanyFunded" type="number" prefix="£" suffix="/month"
                note="This is the company's direct contribution — corporation tax (CT) efficient" />
              <Field onChange={up} label="ISA — current balance" value={form.isaBalance} field="isaBalance" type="number" prefix="£" />
              <Field onChange={up} label="ISA — monthly contribution" value={form.isaContribution} field="isaContribution" type="number" prefix="£" suffix="/month" />
            </>
          )}

          {/* ── STEP 5: Emergency Fund ───────────────────────────────────── */}
          {step === 5 && (
            <>
              <h2 style={{ color: 'white', fontSize: '22px', fontWeight: 600, marginBottom: '24px' }}>Emergency Fund</h2>
              <Field onChange={up} label="Account 1 — name" value={form.efAccount1Name} field="efAccount1Name" />
              <Field onChange={up} label="Account 1 — balance" value={form.efAccount1Balance} field="efAccount1Balance" type="number" prefix="£" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Field onChange={up} label="Account 1 — interest rate" value={form.efAccount1Rate} field="efAccount1Rate" type="number" suffix="%" />
                <Field onChange={up} label="Account 1 — monthly saving" value={form.efAccount1Monthly} field="efAccount1Monthly" type="number" prefix="£" />
              </div>
              <Toggle onChange={up} label="Add second account?" value={form.showSecondAccount} field="showSecondAccount" />
              {form.showSecondAccount && (
                <>
                  <Field onChange={up} label="Account 2 — name" value={form.efAccount2Name} field="efAccount2Name" />
                  <Field onChange={up} label="Account 2 — balance" value={form.efAccount2Balance} field="efAccount2Balance" type="number" prefix="£" />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <Field onChange={up} label="Account 2 — interest rate" value={form.efAccount2Rate} field="efAccount2Rate" type="number" suffix="%" />
                    <Field onChange={up} label="Account 2 — monthly saving" value={form.efAccount2Monthly} field="efAccount2Monthly" type="number" prefix="£" />
                  </div>
                </>
              )}
            </>
          )}

          {/* ── STEP 6: Property & Living Costs ──────────────────────────── */}
          {step === 6 && (
            <>
              <h2 style={{ color: 'white', fontSize: '22px', fontWeight: 600, marginBottom: '24px' }}>Property & Living Costs</h2>
              <Field onChange={up} label="Primary property — estimated value" value={form.propertyValue} field="propertyValue" type="number" prefix="£" />
              <Field onChange={up} label="Mortgage — outstanding balance" value={form.mortgageBalance} field="mortgageBalance" type="number" prefix="£" />
              <Field onChange={up} label="Mortgage — monthly payment" value={form.mortgagePayment} field="mortgagePayment" type="number" prefix="£" suffix="/month" />
              <Field onChange={up} label="Monthly overpayment" value={form.mortgageOP} field="mortgageOP" type="number" prefix="£" suffix="/month" />
              <Field onChange={up} label="Monthly living costs" value={form.livingCosts} field="livingCosts" type="number" prefix="£" suffix="/month"
                note="Groceries, bills, subscriptions, eating out — everything not listed above" />
              <Field onChange={up} label="Sinking funds total (monthly)" value={form.sinkingFundsTotal} field="sinkingFundsTotal" type="number" prefix="£" suffix="/month"
                note="Regular saving for irregular costs — holidays, car, home repairs" />
            </>
          )}

          {/* ── STEP 7: Review & Confirm ─────────────────────────────────── */}
          {step === TOTAL_STEPS && (
            <>
              <h2 style={{ color: 'white', fontSize: '22px', fontWeight: 600, marginBottom: '24px' }}>Review & Confirm</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 32px' }}>
                {[
                  { label: 'Name', value: `${form.firstName} ${form.lastName}` },
                  { label: 'Age', value: form.clientAge },
                  { label: 'Freedom age', value: form.targetFreedomAge },
                  { label: 'Business', value: form.businessName },
                  { label: 'Adviser', value: form.adviserName },
                  ...(form.isCouple ? [{ label: 'Partner', value: `${form.partnerFirstName} ${form.partnerLastName} (${form.partnerAge})` }] : []),
                  { label: 'Salary', value: fmt(num(form.salary)) + '/yr' },
                  { label: 'Dividends', value: fmt(num(form.dividends)) + '/yr' },
                  { label: 'Pension balance', value: fmt(num(form.pensionBalance)) },
                  { label: 'Pension monthly', value: fmt(num(form.pensionMonthly) + num(form.pensionCompanyFunded)) + '/mo' },
                  { label: 'ISA balance', value: fmt(num(form.isaBalance)) },
                  { label: 'Emergency fund', value: fmt(num(form.efAccount1Balance) + num(form.efAccount2Balance)) },
                  { label: 'Property value', value: fmt(num(form.propertyValue)) },
                  { label: 'Mortgage', value: fmt(num(form.mortgageBalance)) },
                  { label: 'Mortgage payment', value: fmt(num(form.mortgagePayment)) + '/mo' },
                  { label: 'Living costs', value: fmt(num(form.livingCosts)) + '/mo' },
                  { label: 'Sinking funds', value: fmt(num(form.sinkingFundsTotal)) + '/mo' },
                ].map(item => (
                  <div key={item.label}>
                    <p style={{ ...labelStyle, marginBottom: '2px' }}>{item.label}</p>
                    <p style={{ color: 'white', fontSize: '15px', fontWeight: 500, margin: 0 }}>{item.value}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Navigation buttons ───────────────────────────────────────── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px', gap: '12px' }}>
            <button
              onClick={() => { if (step === 1) { setScreen('menu'); } else { setStep(step - 1); } }}
              style={{
                padding: '12px 24px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '12px', color: 'white', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              {step === TOTAL_STEPS ? '← Edit' : '← Back'}
            </button>

            {step < TOTAL_STEPS ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && (!form.firstName.trim() || !form.lastName.trim() || !form.clientAge)}
                style={{
                  ...tealBtn, width: 'auto', padding: '12px 32px',
                  opacity: step === 1 && (!form.firstName.trim() || !form.lastName.trim() || !form.clientAge) ? 0.5 : 1,
                }}
              >
                Next →
              </button>
            ) : (
              <button onClick={handleManualConfirm} style={{ ...tealBtn, width: 'auto', padding: '12px 32px' }}>
                Load into session →
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: SESSION SELECTOR
  // ═══════════════════════════════════════════════════════════════════════════
  if (screen === 'session-select') {
    return (
      <div style={{
        minHeight: '100vh', background: '#0a1628', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat, sans-serif',
        padding: '40px 24px',
      }}>
        <p style={{ color: '#64DAD2', fontSize: '15px', fontWeight: 500, marginBottom: '8px' }}>
          {loadedData?.clientName || 'Client'} — loaded
        </p>
        <h2 style={{ color: 'white', fontSize: '28px', fontWeight: 600, margin: '0 0 40px 0' }}>
          Which session are we starting?
        </h2>

        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { session: 1 as const, title: 'Session 1', sub: 'Personal Plan', note: '' },
            { session: 2 as const, title: 'Session 2', sub: 'Business Engine', note: 'Standing orders confirmed before continuing' },
            { session: 3 as const, title: 'Session 3', sub: 'Life Design', note: 'Sessions 1 & 2 complete' },
          ].map(s => (
            <button
              key={s.session}
              onClick={() => handleSessionPick(s.session)}
              style={{
                width: '220px', padding: '32px 24px', background: 'rgba(255,255,255,0.04)',
                border: '2px solid rgba(100,218,210,0.3)', borderRadius: '16px', cursor: 'pointer',
                transition: 'all 0.2s', textAlign: 'center', fontFamily: 'Montserrat, sans-serif',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(100,218,210,0.12)'; e.currentTarget.style.borderColor = '#64DAD2'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(100,218,210,0.3)'; }}
            >
              <p style={{ color: '#64DAD2', fontSize: '13px', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>
                {s.title}
              </p>
              <p style={{ color: 'white', fontSize: '20px', fontWeight: 600, margin: '0 0 8px 0' }}>
                {s.sub}
              </p>
              {s.note && (
                <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', margin: 0 }}>{s.note}</p>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: PRE-SESSION CHECKLIST (non-demo only)
  // ═══════════════════════════════════════════════════════════════════════════
  if (screen === 'checklist' && loadedData && selectedSession) {
    const d = loadedData;
    const efTotal = (d.efAccount1Balance || 0) + (d.efAccount2Balance || 0);
    const efAccounts = [d.efAccount1Name, d.efAccount2Name].filter(Boolean).length || 1;
    const pensionTotal = (d.pensionMonthly || 0) + (d.pensionCompanyFunded || 0);

    const autoChecks = [
      `Client data loaded (${d.clientName}, age ${d.clientAge}, freedom age ${d.targetFreedomAge})`,
      `Income: ${fmt(d.salary)}/yr salary + ${fmt(d.dividends)}/yr dividends`,
      `Pension: ${fmt(d.pensionBalance)} balance · ${fmt(pensionTotal)}/mo contributions`,
      `Emergency fund: ${fmt(efTotal)} across ${efAccounts} account(s)`,
      `Mortgage: ${fmt(d.mortgagePayment)}/mo · ${fmt(d.mortgageBalance)} outstanding`,
    ];

    return (
      <div style={{
        minHeight: '100vh', background: '#0a1628', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat, sans-serif',
        padding: '40px 24px',
      }}>
        <h2 style={{ color: 'white', fontSize: '24px', fontWeight: 600, marginBottom: '8px' }}>
          Pre-session check
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '32px' }}>
          {d.clientName} · Session {selectedSession}
        </p>

        <div style={{ width: '100%', maxWidth: '520px' }}>
          {autoChecks.map((label, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
              <span style={{ color: '#22c55e', fontSize: '18px', lineHeight: '1.4' }}>✓</span>
              <span style={{ color: 'white', fontSize: '14px', lineHeight: '1.5' }}>{label}</span>
            </div>
          ))}

          {/* Manual checks */}
          <div
            onClick={() => setCheckVoyant(!checkVoyant)}
            style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px', cursor: 'pointer' }}
          >
            <span style={{ color: checkVoyant ? '#22c55e' : 'rgba(255,255,255,0.3)', fontSize: '18px', lineHeight: '1.4', width: '18px', textAlign: 'center' }}>
              {checkVoyant ? '✓' : '○'}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: '1.5' }}>
              Voyant plan prepared (manual — adviser confirms)
            </span>
          </div>

          {(selectedSession === 2 || selectedSession === 3) && (
            <div
              onClick={() => setCheckStandingOrders(!checkStandingOrders)}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px', cursor: 'pointer' }}
            >
              <span style={{ color: checkStandingOrders ? '#22c55e' : 'rgba(255,255,255,0.3)', fontSize: '18px', lineHeight: '1.4', width: '18px', textAlign: 'center' }}>
                {checkStandingOrders ? '✓' : '○'}
              </span>
              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: '1.5' }}>
                Standing orders confirmed (Session 2 and 3 only)
              </span>
            </div>
          )}

          <button
            onClick={handleBeginSession}
            style={{ ...tealBtn, marginTop: '28px' }}
          >
            Begin session →
          </button>
        </div>
      </div>
    );
  }

  return null;
}
